import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';

import { Button } from '$partials/ui';
import { IconClose } from '$partials/icons';
import { useKeyboard } from '$hooks/events';
import Select from '$partials/filters/select.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const CommunityFilterDialog = ({
    clearAllFilters,
    disciplineFilter,
    hasFilters,
    isOpen,
    onClose,
    orderByFilter,
    personAlumniYearFilter,
    personTypeFilter,
    regionFilter,
    typeFilter
}) => {
    const dialogRef = useRef();

    const handleClose = () => {
        setTimeout(() => {
            onClose();
        }, 250);
    };

    useKeyboard({
        'Escape': () => isOpen && handleClose()
    }, [isOpen]);

    useEffect(() => {
        // use close/showModal methods to trigger browser's toplayer/inert mode
        const dialogNode = dialogRef.current;
        if (isOpen) {
            if (dialogNode && !dialogNode.open) {
                disableBodyScroll(dialogNode);
                dialogNode.showModal?.();
            }
        } else {
            if (dialogNode && dialogNode.open) {
                enableBodyScroll(dialogNode);
                dialogNode.close?.();
            }
        }
        return () => {
            enableBodyScroll(dialogNode);
        };
    }, [isOpen]);

    return (
        <dialog
            className='w-screen min-w-full m-0 fixed top-0 bottom-0 left-0 min-h-screen'
            ref={dialogRef}>
            {/* Container */}
            <div className='layout-container'>
                {/* Main content */}
                <div className='layout-negative-margin'>
                    <div
                        className='w-full layout-item-padding bg-white'
                        onClick={(e) => e.stopPropagation()}>

                        <div className='flex justify-between items-center pt-20 sm:pt-40'>
                            <p className='type-style-3'>
                                Filters
                            </p>

                            <Button
                                className='block rounded-full'
                                label='Close modal'
                                look='icon'
                                onClick={handleClose}
                                renderIcon={<IconClose />}>
                            </Button>
                        </div>

                        <div>
                            <div className='flex flex-col gap-30 mt-40'>
                                {typeFilter.value === 'persondetailpage' && (
                                    <>
                                        <Select
                                            defaultLabel='Role'
                                            filterModal={true}
                                            label='Person type filter'
                                            name='person-type-filter'
                                            onChange={(value) => personTypeFilter.setValue(value)}
                                            options={[{
                                                label: personTypeFilter.label,
                                                value: ''
                                            },
                                            {
                                                label: 'All',
                                                value: ''
                                            },
                                            ...personTypeFilter.options]}
                                            value={personTypeFilter.value} />

                                        {(personTypeFilter.value === 'alum') && (
                                            <div className='-mt-20 md:mt-0'>
                                                <Select
                                                    defaultLabel='Cohort'
                                                    filterModal={true}
                                                    label='Cohort filter'
                                                    name='person-alumni-year-filter'
                                                    onChange={(value) => personAlumniYearFilter.setValue(value)}
                                                    options={[{
                                                        label: personAlumniYearFilter.label,
                                                        value: ''
                                                    },
                                                    {
                                                        label: 'All',
                                                        value: ''
                                                    },
                                                    ...personAlumniYearFilter.options]}
                                                    value={personAlumniYearFilter.value} />
                                            </div>
                                        )}
                                    </>
                                )}

                                <Select
                                    defaultLabel='Location'
                                    filterModal={true}
                                    label='Location filter'
                                    name='region-filter'
                                    onChange={(value) => regionFilter.setValue(value)}
                                    options={[{
                                        label: regionFilter.label,
                                        value: ''
                                    },
                                    {
                                        label: 'All',
                                        value: ''
                                    },
                                    ...regionFilter.options]}
                                    value={regionFilter.value} />

                                {typeFilter.value === 'persondetailpage' && (
                                    <Select
                                        defaultLabel='Discipline'
                                        filterModal={true}
                                        label='Discipline filter'
                                        name='discipline-filter'
                                        onChange={(value) => disciplineFilter.setValue(value)}
                                        options={[{
                                            label: disciplineFilter.label,
                                            value: ''
                                        },
                                        {
                                            label: 'All',
                                            value: ''
                                        },
                                        ...disciplineFilter.options]}
                                        value={disciplineFilter.value} />
                                )}

                                <Select
                                    defaultLabel='Sort By'
                                    filterModal={true}
                                    label='Sort by filter'
                                    name='sort-by-filter'
                                    onChange={(value) => orderByFilter.setValue(value)}
                                    options={[{
                                        label: orderByFilter.label,
                                        value: ''
                                    },
                                    ...orderByFilter.options]}
                                    value={orderByFilter.value} />
                            </div>
                        </div>

                        {/* Clear button */}
                        {hasFilters && (
                            <button
                                className={'mt-30 pill-button self-start cursor-pointer select-none inline-flex items-center'}
                                onClick={clearAllFilters}
                                type='button'>
                                {'Clear all filters'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </dialog>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    clearAllFilters: PropTypes.func,
    disciplineFilter: PropTypes.object,
    hasFilters: PropTypes.bool,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    orderByFilter: PropTypes.object,
    personAlumniYearFilter: PropTypes.object,
    personTypeFilter: PropTypes.object,
    regionFilter: PropTypes.object,
    typeFilter: PropTypes.object
};

CommunityFilterDialog.propTypes = propTypes;

export default CommunityFilterDialog;
